import {
  CreateGoalSubscriptionModel,
  CreatedGoalSubscriptionModel,
  GetGoalSubscriptionsParamsModel,
  GoalSubscriptionModel,
  GoalSubscriptionVerificationModel,
  GoalSubscriptionsSummaryModel
} from '@models/goalSubscriptions';
import * as Sentry from '@sentry/nextjs';
import { useCallback, useState } from 'react';
import { useImprvApi } from './useImprvApi';

export const useGoalSubscriptionService = () => {
  const imprvApi = useImprvApi();
  const [isPendingGetGoalSubscription, setIsPendingGetGoalSubscription] = useState(false);
  const [isPendingGetGoalSubscriptionsSummary, setIsPendingGetGoalSubscriptionsSummary] =
    useState(false);
  const [isPendingGetGoalSubscriptions, setIsPendingGetGoalSubscriptions] = useState(false);
  const [
    isPendingGetGoalSubscriptionsByGoalEntityId,
    setIsPendingGetGoalSubscriptionsByGoalEntityId
  ] = useState(false);
  const [isPendingCreateGoalSubscription, setIsPendingCreateGoalSubscription] = useState(false);

  const createGoalSubscription = useCallback(
    async (subscription: CreateGoalSubscriptionModel): Promise<CreatedGoalSubscriptionModel> => {
      try {
        setIsPendingCreateGoalSubscription(true);
        return await imprvApi.createGoalSubscription(subscription);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      } finally {
        setIsPendingCreateGoalSubscription(false);
      }
    },
    [imprvApi]
  );

  /**
   * Fetches a goal subscription by its ID
   * @param subscriptionId The ID of the goal subscription to fetch
   * @returns Promise resolving to the GoalSubscriptionModel
   */
  const getGoalSubscription = useCallback(
    async (subscriptionId: number): Promise<GoalSubscriptionModel> => {
      try {
        setIsPendingGetGoalSubscription(true);
        return await imprvApi.getGoalSubscription(subscriptionId);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      } finally {
        setIsPendingGetGoalSubscription(false);
      }
    },
    [imprvApi]
  );

  /**
   * Fetches a list of goal subscriptions based on provided parameters
   * @param params Parameters for filtering and pagination
   * @returns Promise resolving to an array of GoalSubscriptionModel
   */
  const getGoalSubscriptions = useCallback(
    async (
      params: GetGoalSubscriptionsParamsModel
    ): Promise<{ goalSubscriptions: GoalSubscriptionModel[] }> => {
      try {
        setIsPendingGetGoalSubscriptions(true);
        return await imprvApi.getGoalSubscriptions(params);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      } finally {
        setIsPendingGetGoalSubscriptions(false);
      }
    },
    [imprvApi]
  );

  /**
   * Fetches a goal subscription verification by its ID
   * @param verificationId The ID of the verification to fetch
   * @returns Promise resolving to the GoalSubscriptionVerificationModel
   */
  const getGoalSubscriptionVerification = useCallback(
    async (verificationId: string): Promise<GoalSubscriptionVerificationModel> => {
      try {
        return await imprvApi.getGoalSubscriptionVerification(verificationId);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  const getGoalSubscriptionsSummary =
    useCallback(async (): Promise<GoalSubscriptionsSummaryModel> => {
      try {
        setIsPendingGetGoalSubscriptionsSummary(true);
        return await imprvApi.getGoalSubscriptionsSummary();
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      } finally {
        setIsPendingGetGoalSubscriptionsSummary(false);
      }
    }, [imprvApi]);

  /**
   * Fetches goal subscriptions by goal entity ID
   * @param goalEntityId The entity ID of the goal
   * @param statuses Optional array of subscription statuses to filter by
   * @returns Promise resolving to an array of GoalSubscriptionModel
   */
  const getGoalSubscriptionsByGoalEntityId = useCallback(
    async (goalEntityId: string, statuses?: string[]): Promise<GoalSubscriptionModel[]> => {
      try {
        setIsPendingGetGoalSubscriptionsByGoalEntityId(true);
        return await imprvApi.getGoalSubscriptionsByGoalEntityId(goalEntityId, statuses);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      } finally {
        setIsPendingGetGoalSubscriptionsByGoalEntityId(false);
      }
    },
    [imprvApi]
  );

  return {
    getGoalSubscription,
    isPendingGetGoalSubscription,
    getGoalSubscriptions,
    isPendingGetGoalSubscriptions,
    getGoalSubscriptionVerification,
    getGoalSubscriptionsSummary,
    isPendingGetGoalSubscriptionsSummary,
    getGoalSubscriptionsByGoalEntityId,
    isPendingGetGoalSubscriptionsByGoalEntityId,
    createGoalSubscription,
    isPendingCreateGoalSubscription
  };
};
